import { SortDirection } from 'components/Table/types';
import { queryClient } from 'index';
import { useEffect, useRef } from 'react';
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from 'react-query';
import { toast } from 'react-toastify';
import {
  getToastErrorMessage,
  isAnswerLost,
  LogyxError,
  LogyxErrorId,
  readErrorStatus,
  readLogyxError,
} from 'services/api/errors';
import {
  captureLostAnswer,
  LostAnswerEndpoint,
} from 'services/api/lostAnswerCapture';
import { ReactMutationKeys } from 'services/api/reactMutationKeys';
import { ReactQueryKeys } from 'services/api/reactQueryKeys';
import i18n from 'providers/i18n/i18n';
import {
  changeOrderStatus,
  getOrderById,
  getOrders,
  retryOrder,
} from 'services/OrdersService';
import { AppMode } from 'store/Common/reducers/common';
import { getNextPageParam } from 'utils/getNextPageParam';
import { useTranslation } from 'react-i18next';
import { IOrderOutcome, OrderStatus } from 'types/Orders.types';
import { getOrderPollInterval, isOrderBeingSettled } from 'utils/orderUtils';
import { outcomeUnknownText } from 'utils/hooks/useOrderRefusal';

// While a loaded order is SENDING the list asks again, so it settles in view.
export const LIST_POLL_INTERVAL_MS = 15 * 1000;

interface IOrdersPage {
  orders?: { status?: string }[];
}

export const hasSendingOrder = (data: InfiniteData<IOrdersPage> | undefined) =>
  !!data?.pages?.some((page) =>
    page?.orders?.some((order) => order?.status === OrderStatus.SENDING)
  );

export const useGetSupplierOrdersInfinite = (
  appMode: AppMode,
  companyId: number,
  perPage: number,
  searchBy?: string,
  sortBy?: string,
  sortDirection?: SortDirection
) =>
  useInfiniteQuery({
    queryKey: [
      ReactQueryKeys.GET_SUPPLIER_ORDERS,
      companyId,
      perPage,
      searchBy,
      sortBy,
      sortDirection,
    ],
    queryFn: ({ pageParam = 1 }) => {
      return getOrders(
        appMode,
        companyId,
        pageParam,
        perPage,
        searchBy,
        sortBy,
        sortDirection
      );
    },
    getNextPageParam,
    refetchInterval: (data) =>
      hasSendingOrder(data) ? LIST_POLL_INTERVAL_MS : false,
  });

// The id of a toast about an order's status. Retry's answer and the settle
// announcement below can both tell the same outcome: a slow Retry whose order
// the page saw SENDING meanwhile (a refetch on a window focus). react-toastify
// does not show a toast whose id is still on screen, so it is told once.
const orderToastId = (
  id: number | string | undefined,
  status: string | undefined
) => `order-${id}-${status}`;

// When an order settles while its page is open, a toast says what happened. A
// status the page opened with is not announced, and neither is another order a
// link opens in the same page.
interface IAnnouncedOrder extends IOrderOutcome {
  id?: number;
  status?: string;
  internal_number?: string | null;
}

const useAnnounceSettledOrder = (order: IAnnouncedOrder | undefined) => {
  const { t } = useTranslation();
  const seenRef = useRef<{ id?: number; status?: string }>({});

  useEffect(() => {
    if (!order?.id) return;
    const seen = seenRef.current;
    seenRef.current = { id: order.id, status: order.status };
    if (
      seen.id !== order.id ||
      !isOrderBeingSettled(seen.status) ||
      seen.status === order.status
    ) {
      return;
    }
    const reason = order.outcome_message;
    const toastId = orderToastId(order.id, order.status);
    if (order.status === OrderStatus.SUCCESSFUL) {
      toast.success(
        order.internal_number
          ? t('The order was placed as {{number}}.', {
              number: order.internal_number,
            })
          : t('The order was placed.'),
        { toastId }
      );
    } else if (order.status === OrderStatus.FAILED) {
      toast.error(
        reason
          ? t('The order could not be placed: {{reason}}', { reason })
          : t('The order could not be placed.'),
        { toastId }
      );
    } else if (order.status === OrderStatus.UNKNOWN) {
      toast.warning(
        t(
          'The supplier did not confirm the order. It is being checked; do not order it again.'
        ),
        { toastId }
      );
    }
  }, [order?.id, order?.status]);
};

export const useGetSingleOrder = (orderId: string, appMode?: AppMode) => {
  // When the page first saw the order SENDING: the poll slows down after a minute.
  const sendingSinceRef = useRef<number | null>(null);

  const query = useQuery({
    queryKey: [ReactQueryKeys.GET_SINGLE_ORDER, orderId, i18n.resolvedLanguage],
    queryFn: () => {
      return getOrderById(orderId, appMode);
    },
    enabled: !!orderId,
    // A failed poll keeps the last status on screen; the next one runs as planned.
    refetchInterval: (data) => {
      const status = (data as { status?: string } | undefined)?.status;
      if (status !== OrderStatus.SENDING) {
        sendingSinceRef.current = null;
      } else if (sendingSinceRef.current === null) {
        sendingSinceRef.current = Date.now();
      }
      const sendingForMs =
        sendingSinceRef.current === null
          ? 0
          : Date.now() - sendingSinceRef.current;
      return getOrderPollInterval(status, sendingForMs);
    },
  });

  useAnnounceSettledOrder(query.data);

  return query;
};

export const useChangeOrderStatus = () => {
  const { t } = useTranslation();
  return useMutation(
    (params: { id: string; status: string }) =>
      changeOrderStatus(params.id, params.status),
    {
      onSuccess: () => {
        toast.success(t('Status changed successfully'));
        queryClient.invalidateQueries(ReactQueryKeys.GET_SINGLE_ORDER);
        queryClient.invalidateQueries(ReactQueryKeys.GET_SUPPLIER_ORDERS);
        queryClient.invalidateQueries(ReactQueryKeys.GET_BUYER_ORDERS);
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error), {
          toastId: ReactMutationKeys.CHANGE_ORDER_STATUS,
        });
        queryClient.invalidateQueries(ReactQueryKeys.GET_SINGLE_ORDER);
      },
      mutationKey: ReactMutationKeys.CHANGE_ORDER_STATUS,
    }
  );
};

// POST /v1/orders/{id}/resend (logix-core docs/ordering.md, "Resending"). Its
// answer carries the order's fields as a list of one entry. Its toasts share
// the settle announcement's ids (orderToastId).
export const useRetryOrder = () => {
  const { t } = useTranslation();
  return useMutation((params: { id: string }) => retryOrder(params.id), {
    onSuccess: (data, params) => {
      const order = Array.isArray(data) ? data[0] : data;
      const number = order?.internal_number || order?.order;
      toast.success(
        number
          ? t('The order was placed as {{number}}.', { number })
          : t('Order retried successfully'),
        { toastId: orderToastId(params.id, OrderStatus.SUCCESSFUL) }
      );
    },
    onError: (error: unknown, params) => {
      const answer = readLogyxError(error);
      // The order's status, when the answer names it: in errors[0], as `data` is
      // a list when Retry's own send did not go through.
      const status = readErrorStatus(answer);
      const toastId = status ? orderToastId(params.id, status) : undefined;
      if (answer.errorId === LogyxErrorId.ORDER_SENDING) {
        toast.info(
          t('This order is still being sent. Its status updates on this page.'),
          { toastId }
        );
      } else if (answer.errorId === LogyxErrorId.ORDER_OUTCOME_UNKNOWN) {
        toast.warning(t(outcomeUnknownText(answer)), { toastId });
      } else if (isAnswerLost(answer)) {
        // One Sentry event, with the request id (lostAnswerCapture.ts).
        captureLostAnswer(error, {
          endpoint: LostAnswerEndpoint.RESEND,
          orderId: params.id,
        });
        toast.warning(
          t(
            'No answer from the server. The order may have been sent; this page shows its status.'
          )
        );
      } else {
        toast.error(getToastErrorMessage(error as LogyxError), {
          toastId: toastId ?? ReactMutationKeys.RETRY_ORDER,
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(ReactQueryKeys.GET_SINGLE_ORDER);
      queryClient.invalidateQueries(ReactQueryKeys.GET_SUPPLIER_ORDERS);
      queryClient.invalidateQueries(ReactQueryKeys.GET_BUYER_ORDERS);
    },
    mutationKey: ReactMutationKeys.RETRY_ORDER,
  });
};
